import z from "zod";
import { AvatarSchema } from "rdx/modules/users/schemas/avatar";
import { RecommendationKindEnum, RecommendationStatusEnum, RecommendationSubjectTypeEnum } from "./enums";

const RecommendationSponsorSchema = z.object({
  id: z.number(),
  fullName: z.string(),
});

const RecommendationUserSubjectSchema = z.object({
  type: z.literal(RecommendationSubjectTypeEnum.USER),
  id: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  avatar: AvatarSchema,
  currentRank: z.number(),
  level: z.number().nullable(),
  sponsor: RecommendationSponsorSchema.nullable(),
});

const RecommendationProjectSubjectSchema = z.object({
  type: z.literal(RecommendationSubjectTypeEnum.PROJECT),
  id: z.number(),
  agent: z.object({
    id: z.number(),
    fullName: z.string(),
  }),
  fullName: z.string(),
});

export const RecommendationSubjectSchema = z.discriminatedUnion("type", [
  RecommendationUserSubjectSchema,
  RecommendationProjectSubjectSchema,
]);

const ReEngageMetadataSchema = z.object({
  daysInactive: z.number(),
  lastActiveAt: z.string(),
});

const StalledLeadsMetadataSchema = z.object({
  leadIds: z.array(z.number()),
  oldestDays: z.number(),
  severity: z.number(),
  stalledCount: z.number(),
});

const StalledProjectMetadataSchema = z.object({
  agentId: z.number(),
  daysInStage: z.number(),
  enteredAt: z.string(),
  severity: z.number(),
  stageSlug: z.string(),
});

const RankUpPushMetadataSchema = z.object({
  label: z.string(),
  severity: z.number(),
  overallProgress: z.number(),
  targetRank: z.number(),
  targetRankTitle: z.string(),
  prvPct: z.number(),
  brvPct: z.number().nullable(),
  qlPct: z.number().nullable(),
});

const StalledOnboardingMetadataSchema = z.object({
  label: z.string(),
  severity: z.number(),
  ageDays: z.number(),
  triggerLogin: z.boolean(),
  triggerNoLeads: z.boolean(),
  triggerOnboarding: z.boolean(),
});

export const RecommendationSummarySchema = z.object({
  id: z.number(),
  kind: z.nativeEnum(RecommendationKindEnum),
  label: z.string(),
  status: z.nativeEnum(RecommendationStatusEnum),
});

const RecommendationBaseSchema = z.object({
  id: z.number(),
  label: z.string(),
  status: z.nativeEnum(RecommendationStatusEnum),
  score: z.number(),
  createdAt: z.string(),
});

const ReEngageRecommendationSchema = RecommendationBaseSchema.extend({
  kind: z.literal(RecommendationKindEnum.RE_ENGAGE),
  metadata: ReEngageMetadataSchema,
  subject: RecommendationUserSubjectSchema,
});

const StalledLeadsRecommendationSchema = RecommendationBaseSchema.extend({
  kind: z.literal(RecommendationKindEnum.STALLED_LEADS),
  metadata: StalledLeadsMetadataSchema,
  subject: RecommendationUserSubjectSchema,
});

const StalledProjectRecommendationSchema = RecommendationBaseSchema.extend({
  kind: z.literal(RecommendationKindEnum.STALLED_PROJECT),
  metadata: StalledProjectMetadataSchema,
  subject: RecommendationProjectSubjectSchema,
});

const RankUpPushRecommendationSchema = RecommendationBaseSchema.extend({
  kind: z.literal(RecommendationKindEnum.RANK_UP_PUSH),
  metadata: RankUpPushMetadataSchema,
  subject: RecommendationUserSubjectSchema,
});

const StalledOnboardingRecommendationSchema = RecommendationBaseSchema.extend({
  kind: z.literal(RecommendationKindEnum.STALLED_ONBOARDING),
  metadata: StalledOnboardingMetadataSchema,
  subject: RecommendationUserSubjectSchema,
});

export const RecommendationSchema = z.discriminatedUnion("kind", [
  ReEngageRecommendationSchema,
  StalledLeadsRecommendationSchema,
  StalledProjectRecommendationSchema,
  RankUpPushRecommendationSchema,
  StalledOnboardingRecommendationSchema,
]);

export const RecommendationsCountsSchema = z.object({
  active: z.number(),
  byKind: z.record(z.string(), z.number()),
});

export const RecommendationsPagingSchema = z.object({
  itemCount: z.number(),
  pageNumber: z.number(),
  pageSize: z.number(),
  hasMore: z.boolean(),
});

export const RecommendationsPayloadSchema = z.object({
  items: z.array(RecommendationSchema),
  counts: RecommendationsCountsSchema,
});

export const GetRecommendationsResponseSchema = z.object({
  body: RecommendationsPayloadSchema,
  paging: RecommendationsPagingSchema.optional(),
});

export const PatchRecommendationResponseSchema = z.object({
  body: RecommendationSchema,
});
